import React, {MouseEvent, MutableRefObject, useCallback, useState} from 'react';
import intl from "react-intl-universal";


import {useAppState, useAuthService} from "@stores/AppStateContext";

import {useNotification} from "../../../../../hooks/useNotification";
import {Button} from "../../../../../components/Button";
import {AWSAPIError} from "../../../../../../utils/types";

import {MODALS} from "../../../modal-names";

export interface ResendConfirmationCodeProps {
    setErrors: ({}) => void;
    getEmail: () => string;
    emailFieldRef?: MutableRefObject<HTMLInputElement | undefined>;
}

export const ResendConfirmationCode = (
    {
        setErrors,
        getEmail,
        emailFieldRef,
    }: ResendConfirmationCodeProps) =>
{
    const appState = useAppState();
    const {enqueueSnackbar} = useNotification();
    const authService = useAuthService();
    const [isProcessingResend, setIsProcessingResend] = useState(false);

    const handleResendClick = useCallback((event: MouseEvent) => {
        event.preventDefault();
        if (isProcessingResend) {
            return;
        }
        setErrors({});
        setIsProcessingResend(true);

        let username = getEmail();

        if (!username) {
            setErrors({
                email: intl.get('sign_up.email_required').d('Email is required'),
            });
            emailFieldRef?.current?.focus();
            setIsProcessingResend(false);
            return;
        }

        authService.resendSignUpCode(username)
            .then(() => {
                setIsProcessingResend(false);
                enqueueSnackbar({
                    message: intl.get('sign_up.confirm_resent_success').d('We have sent you a confirmation code. Please check your email'),
                    variant: 'success',
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'right',
                    }
                });
            })
            .catch((error: any) => {
                setIsProcessingResend(false);

                switch ((error as AWSAPIError).name) {
                    case 'UserNotFoundException': {
                        setErrors({
                            email: intl.get('aws_api_error.UserNotFoundException').d("The email or password you entered doesn't match our records. Please double-check and try again."),
                        });
                        break;
                    }
                    case 'LimitExceededException': {
                        appState.setModal({name: MODALS.LOCKED, params:{
                            reason: 'confirm_resend_limit'
                        }});
                        break;
                    }
                    case 'InvalidParameterException': {
                        switch ((error as Error).message) {
                            case 'User is already confirmed.':
                                setErrors({
                                    email: intl.get('aws_api_error.InvalidParameterException.user_is_confirmed').d('User is already confirmed.'),
                                });
                                break;
                            default:
                                setErrors({
                                    email: (error as Error).message,
                                });
                        }
                        break;
                    }
                    default:
                        setErrors({
                            email: (error as Error).message,
                        });
                }
            });
    }, [isProcessingResend]);

    return (
        <Button
            className={'Auth__inline-btn'}
            onClick={handleResendClick}
            text={intl.get('sign_up.resend_btn').d('Resend')}
            style={'plain'}
            type={'primary'}
            size={'sm'}
            loading={isProcessingResend}
        />
    )
}
